import { zodResolver } from '@hookform/resolvers/zod';
import { PoleKoduSps } from '@/components/PoleKoduSps';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Ban, Boxes, Clock, ImageUp, Images, KeyRound, Loader2, Pencil, Plus, RotateCcw, Truck } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { PasekZapelnienia } from '@/components/PasekZapelnienia';
import { ObrazekTowaru } from '@/components/ObrazekTowaru';
import { ZarzadzanieZdjeciami } from '@/components/ZarzadzanieZdjeciami';
import { fetchWysylkiObrazkow, type WysylkaObrazka } from '@/lib/items-api';
import { ZmianaNumeruTowaru } from '@/components/ZmianaNumeruTowaru';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ZarzadzanieDostawcami } from '@/components/ZarzadzanieDostawcami';
import { fetchDostawcy } from '@/lib/suppliers-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { useSearchParams } from 'react-router';
import {
  checkItemNumber,
  createItem,
  fetchItemDetail,
  fetchItemGroups,
  listItems,
  setItemObsolete,
  updateItem,
  type ItemGroupOption,
  type ItemRow,
} from '@/lib/items-api';

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('pl-PL', { dateStyle: 'short' }) : '—';

/** Etykieta grupy dla listy wyboru: podgrupa zawsze z nazwą grupy nadrzędnej. */
const groupLabel = (g: ItemGroupOption): string =>
  g.isSubGroup ? `${g.parentName ?? '?'} › ${g.name} (${g.code})` : `${g.name} (${g.code})`;

// ─────────────────────────────── formularz ───────────────────────────────────

/**
 * NUMER TOWARU jest wpisywany, nie generowany.
 *
 * `Items.ITEMNUMBER` to kod katalogowy nadawany przez człowieka — u klientów realnie
 * `'REK-NITRYL-M'`. Portal nie ma czego podpowiadać; może tylko sprawdzić kolizję.
 */
const FormSchema = z.object({
  itemNumber: z
    .string()
    .trim()
    .min(1, 'Podaj numer towaru')
    .max(32, 'Najwyżej 32 znaki — SPS obciąłby dłuższy bez ostrzeżenia'),
  name: z.string().trim().min(1, 'Podaj nazwę towaru').max(255),
  unitCost: z
    .string()
    .trim()
    .regex(/^\d{1,10}([.,]\d{1,2})?$/, 'Cena w formacie 19.90')
    .optional()
    .or(z.literal('')),
  itemGroupId: z.string().optional(),
  supplierPartNumber: z.string().trim().max(50).optional(),
  /** ⚠️ Sufit 32 znaki bierze się z kolumny SPS, nie z kaprysu — patrz `suppliers/dostawcy.ts`. */
  supplierNumber: z.string().trim().max(32).optional(),
  brand: z.string().trim().max(32).optional(),
});

type FormValues = z.infer<typeof FormSchema>;

function ItemModal({
  editing,
  groupId,
  groups,
  onClose,
  onSaved,
}: {
  editing: ItemRow | null;
  groupId: string;
  groups: ItemGroupOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  /**
   * ⚠️⚠️ SŁOWNIK DOSTAWCÓW TEJ WYSPY. Bywa PUSTY i to nie jest usterka — zmierzone 22.09.2026:
   * Tyco ma dwa wpisy, IKEA ani jednego. Ekran mówi wtedy wprost, że wyspa nie ma dostawców,
   * zamiast pokazywać pustą listę wyboru, która wygląda jak coś, co się nie wczytało.
   */
  const slownik = useQuery({
    queryKey: ['dostawcy', groupId],
    queryFn: () => fetchDostawcy(groupId),
    enabled: groupId !== '',
  });
  const dostawcy = slownik.data?.dostawcy ?? [];

  /**
   * ⚠️ NUMER, KTÓREGO NIE MA W SŁOWNIKU, A TOWAR GO MA. Zostaje na liście wyboru, bo inaczej samo
   * otwarcie edycji zdjęłoby przypisanie, którego nikt nie ruszał — a w SPS taki wiersz jest
   * poprawny, bo nie ma tam klucza obcego.
   */
  const nieznanyDostawca =
    editing?.supplierNumber != null &&
    !dostawcy.some((d) => d.supplierNumber.toLowerCase() === editing.supplierNumber!.toLowerCase())
      ? editing.supplierNumber
      : null;

  const podpowiedzDostawcy =
    slownik.data !== undefined && dostawcy.length === 0 ? t('itm.noSuppliersOnIsland') : undefined;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      itemNumber: editing?.itemNumber ?? '',
      name: editing?.name ?? '',
      unitCost: editing?.unitCost ?? '',
      itemGroupId: editing?.group?.id ?? '',
      supplierPartNumber: editing?.supplierPartNumber ?? '',
      supplierNumber: editing?.supplierNumber ?? '',
      /**
       * ⚠️⚠️ Z WIERSZA, NIE PUSTE NA SZTYWNO. Puste pole szło do zapisu jako `BRAND` i KASOWAŁO
       * markę w bazie klienta przy każdej edycji — choćby samej ceny. Zmierzone na wyspie DBW
       * 25.08.2026: wpisana marka doszła do trzech maszyn, a okno pokazało puste pole, bo trasa
       * listy marki nie zwracała.
       */
      brand: editing?.brand ?? '',
    },
  });

  /**
   * ⚠️⚠️ DWIE WARTOŚCI, NIE JEDNA, i to nie jest nadmiar. `numerWpisany` idzie do POLA, więc NIE
   * może być przycinana: gdyby była, spacja między wyrazami znikałaby w chwili wpisania
   * (bo w tej chwili jest na końcu wartości), a spacja w środku kodu jest dozwolona.
   * `numberTyped` — przycięta — służy sprawdzeniu kolizji, bo o kolizji decyduje postać DO ZAPISU.
   */
  const numerWpisany = watch('itemNumber') ?? '';
  const numberTyped = numerWpisany.trim();

  const numberCheck = useQuery({
    queryKey: ['items', 'check-number', groupId, numberTyped],
    queryFn: () => checkItemNumber(groupId, numberTyped),
    enabled: editing === null && numberTyped.length > 0,
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    const cost = values.unitCost?.trim() ? values.unitCost.trim() : undefined;
    try {
      if (editing) {
        await updateItem(editing.id, {
          name: values.name,
          ...(cost ? { unitCost: cost } : {}),
          itemGroupId: values.itemGroupId ? values.itemGroupId : null,
          supplierPartNumber: values.supplierPartNumber?.trim() ? values.supplierPartNumber.trim() : null,
          /** ⚠️ `null` = zdjęcie dostawcy z towaru; portal zamieni je na pusty napis dla SPS. */
          supplierNumber: values.supplierNumber?.trim() ? values.supplierNumber.trim() : null,
          brand: values.brand?.trim() ? values.brand.trim() : null,
        });
        toast.success(t('itm.saved'));
      } else {
        const res = await createItem({
          groupId,
          itemNumber: values.itemNumber,
          name: values.name,
          ...(cost ? { unitCost: cost } : {}),
          ...(values.itemGroupId ? { itemGroupId: values.itemGroupId } : {}),
          ...(values.supplierPartNumber?.trim() ? { supplierPartNumber: values.supplierPartNumber.trim() } : {}),
          ...(values.supplierNumber?.trim() ? { supplierNumber: values.supplierNumber.trim() } : {}),
          ...(values.brand?.trim() ? { brand: values.brand.trim() } : {}),
        });
        toast.success(`${t('itm.created')} (${res.itemNumber})`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  const chosen = groups.find((g) => g.id === watch('itemGroupId'));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={editing ? t('itm.editTitle') : t('itm.addTitle')}
        /** ⚠️ BEZ OPISU PRZY ZAKŁADANIU — to samo zdanie powtarzało się pod polem; reguła jest teraz przy polu. */
        description={editing ? `${t('itm.number')}: ${editing.itemNumber}` : undefined}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormGrid>
            {/* Numer edytowalny TYLKO przy tworzeniu: to klucz naturalny rozsiany po
                planogramie, limitach i historii wydań — jego zmiana to nie edycja. */}
            {!editing && (
              <Field
                label={t('itm.number')}
                htmlFor="itemNumber"
                required
                error={errors.itemNumber?.message}
              >
                {/* ⚠️ Ta sama reguła co przy kodzie grupy — jedno pole, jeden komponent. */}
                <PoleKoduSps
                  id="itemNumber"
                  autoFocus
                  placeholder="np. REK-NITRYL-M"
                  value={numerWpisany}
                  onChange={(v) => setValue('itemNumber', v, { shouldValidate: true })}
                />
                {numberTyped.length > 0 && numberCheck.data && !numberCheck.data.free && (
                  <p className="mt-1 text-xs text-[var(--destructive)]">
                    {t('itm.numberTaken').replace('{name}', numberCheck.data.takenBy?.name ?? '?')}
                  </p>
                )}
                {numberTyped.length > 0 && numberCheck.data?.free && numberCheck.data.pending && (
                  <p className="mt-1 text-xs text-[var(--warning)]">{t('itm.numberPending')}</p>
                )}
              </Field>
            )}

            <Field label={t('itm.name')} htmlFor="name" required error={errors.name?.message}>
              <Input id="name" autoComplete="off" autoFocus={!!editing} {...register('name')} />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('itm.unitCost')} htmlFor="unitCost" error={errors.unitCost?.message}>
                <Input id="unitCost" autoComplete="off" placeholder="19.90" {...register('unitCost')} />
              </Field>
              <Field label={t('itm.brand')} htmlFor="brand" error={errors.brand?.message}>
                <Input id="brand" autoComplete="off" {...register('brand')} />
              </Field>
            </div>

            <Field label={t('itm.group')} htmlFor="itemGroupId" hint={chosen?.isSubGroup ? t('itm.subGroupHint') : undefined}>
              <Select id="itemGroupId" {...register('itemGroupId')}>
                <option value="">{t('itm.noGroup')}</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {groupLabel(g)}
                  </option>
                ))}
              </Select>
            </Field>

            {/*
              ⚠️⚠️ DOSTAWCA NAD NUMEREM U DOSTAWCY, bo numer bez dostawcy nie znaczy nic. Lista
              wyboru pochodzi ze słownika `Suppliers` tej wyspy — portal nie pozwala wpisać numeru
              z palca, bo w SPS nie ma tam klucza obcego i literówka zostałaby przyjęta w ciszy.
            */}
            <Field label={t('itm.supplier')} htmlFor="supplierNumber" hint={podpowiedzDostawcy}>
              <Select id="supplierNumber" {...register('supplierNumber')}>
                <option value="">{t('itm.noSupplier')}</option>
                {/*
                  ⚠️ DOSTAWCA SPOZA SŁOWNIKA ZOSTAJE NA LIŚCIE, gdy towar już go ma. Inaczej
                  otwarcie edycji po cichu zdejmowałoby przypisanie, którego nikt nie ruszał.
                */}
                {nieznanyDostawca !== null && (
                  <option value={nieznanyDostawca}>
                    {nieznanyDostawca} {t('itm.supplierUnknown')}
                  </option>
                )}
                {dostawcy
                  .filter((d) => d.doPrzypisania)
                  .map((d) => (
                    <option key={d.supplierNumber} value={d.supplierNumber}>
                      {d.name ?? d.supplierNumber}
                    </option>
                  ))}
              </Select>
            </Field>

            <Field
              label={t('itm.supplierPart')}
              htmlFor="supplierPartNumber"
              error={errors.supplierPartNumber?.message}
              hint={t('itm.supplierPartHint')}
            >
              <Input id="supplierPartNumber" autoComplete="off" {...register('supplierPartNumber')} />
            </Field>

            <ModalError>{serverError}</ModalError>
          </FormGrid>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── rozbicie na automaty ────────────────────────────

function DetailModal({ item, groupId, onClose }: { item: ItemRow; groupId: string; onClose: () => void }) {
  const { t } = useI18n();
  const detail = useQuery({
    queryKey: ['items', item.id, 'detail', groupId],
    queryFn: () => fetchItemDetail(item.id, groupId),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={`${item.name}`}
        description={`${t('itm.number')}: ${item.itemNumber}${item.group ? ` · ${item.group.name}` : ''}`}
        className="w-[min(52rem,calc(100vw-2rem))]"
      >
        {detail.isPending ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : (
          <div className="space-y-3">
            {detail.data?.item.group?.isSubGroup && (
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('itm.subGroupIsgid').replace('{isgid}', detail.data.item.group.isgid ?? '?')}
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-1.5">{t('itm.machine')}</th>
                  {/*
                    ⚠️ ODSTĘP PO PRAWEJ PRZY KOLUMNACH WYRÓWNANYCH DO PRAWEJ — zgłoszone jako
                    „za każdym razem jest ten szeroki myślnik". Kolumna wyrównana do prawej kładzie
                    treść na swojej PRAWEJ krawędzi, czyli dokładnie przy treści kolumny następnej.
                    Bez tego odstępu nagłówki zlewały się w „Próg SPSStan", a myślnik „brak progu"
                    wyglądał na część paska zapełnienia obok. Jeden brakujący odstęp, dwa objawy.
                  */}
                  <th className="py-1.5 pr-4 text-right">{t('itm.stock')}</th>
                  <th className="py-1.5 pr-4 text-right">{t('itm.threshold')}</th>
                  <th className="py-1.5">{t('itm.state')}</th>
                </tr>
              </thead>
              <tbody>
                {(detail.data?.machines ?? []).map((m) => (
                  <tr key={m.machineId} className="border-b border-border/60">
                    <td className="py-1.5">
                      <span className="font-mono text-xs text-muted-foreground">VMID {m.vmid}</span> {m.machineName}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {m.presence === 'absent' ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <>
                          {m.qtyCurrent}
                          {m.qtyMax > 0 && <span className="text-muted-foreground">/{m.qtyMax}</span>}
                        </>
                      )}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">
                      {m.criticalLevel > 0 ? (
                        m.criticalLevel
                      ) : (
                        <span className="text-muted-foreground" title={t('itm.noThresholdHint')}>
                          —
                        </span>
                      )}
                    </td>
                    <td className="py-1.5">
                      {/* Trzy RÓŻNE sytuacje, których zlanie w jedną było błędem pierwszej
                          wersji: brak w bębnie ≠ brak pojemności ≠ pusto. */}
                      {m.presence === 'absent' ? (
                        <span className="text-xs text-muted-foreground">{t('itm.absent')}</span>
                      ) : m.presence === 'noCapacity' ? (
                        <span className="text-xs text-muted-foreground">{t('itm.noCapacity')}</span>
                      ) : (
                        /* ⚠️ PASEK TAKŻE TUTAJ, i to jest sedno zgłoszenia „nie można zobaczyć
                           per maszyna". Rozbicie istniało, ale oddawało wyłącznie liczby —
                           odpowiedź na „gdzie się kończy" trzeba było złożyć w głowie z pięciu
                           wierszy. Pasek jest ten sam co na liście (`PasekZapelnienia`), więc
                           kolor znaczy na obu ekranach dokładnie to samo. */
                        <span className="flex items-center gap-2">
                          {m.fillPercent !== null && <PasekZapelnienia percent={m.fillPercent} />}
                          {m.belowThreshold === true ? (
                            <Badge variant="warning">{t('itm.belowThreshold')}</Badge>
                          ) : m.belowThreshold === null ? (
                            <span className="text-xs text-muted-foreground" title={t('itm.noThresholdHint')}>
                              {t('itm.noThreshold')}
                            </span>
                          ) : (
                            <span className="tabular-nums text-xs text-muted-foreground">{m.fillPercent}%</span>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {(detail.data?.machines ?? []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-sm text-muted-foreground">
                      {t('itm.notAssigned')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── wycofanie towaru ────────────────────────────────

function ObsoleteModal({
  item,
  groupId,
  onClose,
  onSaved,
}: {
  item: ItemRow;
  groupId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retiring = !item.obsolete;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await setItemObsolete(item.id, groupId, retiring);
      toast.success(retiring ? t('itm.retired') : t('itm.restored'));
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={retiring ? t('itm.retireTitle') : t('itm.restoreTitle')}
        description={`${item.name} (${item.itemNumber})`}
      >
        <div className="space-y-3 text-sm">
          <p>{retiring ? t('itm.retireWhy') : t('itm.restoreWhy')}</p>
          {/* Nie da się tego pominąć: kolumny OBSOLETE nie ma w schemacie odczytanym z SPS. */}
          <div className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
            {t('itm.obsoleteUnverified')}
          </div>
          <ModalError>{error}</ModalError>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={run} disabled={busy}>
            {busy && <Loader2 className="animate-spin" />}
            {retiring ? t('itm.retireConfirm') : t('itm.restoreConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── strona ──────────────────────────────────────

export function Items() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const {groupId} = useIslands();

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 25,
    sort: 'name',
    dir: 'asc',
  });
  /**
   * GRUPA Z ADRESU — po to, żeby dało się tu wejść z ekranu grup towarowych.
   *
   * ⚠️⚠️ ZGŁOSZONE 01.09.2026: „kliknięcie w grupie artykułów na daną grupę — niech przenosi do
   * zakładki artykuły z wyfiltrowaną tą grupą". Bez tego trzeba było wejść tutaj i wybrać tę samą
   * grupę z listy rozwijanej, czyli zrobić drugi raz to, co się właśnie kliknęło.
   *
   * ⚠️ CZYTAMY TYLKO NA WEJŚCIU (`useState` z wartością początkową), a nie w każdym renderze: filtr
   * jest po tym zwykłym stanem ekranu, więc zmiana w liście rozwijanej nie może być cofana przez
   * adres, który został w pasku.
   */
  const [szukajka, ustawSzukajke] = useSearchParams();
  const [itemGroupId, setItemGroupId] = useState(szukajka.get('grupa') ?? '');
  const [assigned, setAssigned] = useState('');
  const [fill, setFill] = useState('');
  const [status, setStatus] = useState<'active' | 'obsolete' | 'all'>('active');
  const [onlyDuplicates, setOnlyDuplicates] = useState(false);

  const [adding, setAdding] = useState(false);
  const [zdjecia, setZdjecia] = useState(false);
  const [dostawcyOtwarte, setDostawcyOtwarte] = useState(false);
  const [editing, setEditing] = useState<ItemRow | null>(null);
  const [detail, setDetail] = useState<ItemRow | null>(null);
  const [obsoleting, setObsoleting] = useState<ItemRow | null>(null);
  const [numerFor, setNumerFor] = useState<ItemRow | null>(null);
  const [obrazekFor, setObrazekFor] = useState<ItemRow | null>(null);

  /**
   * ⚠️⚠️ WYSYŁKI ZDJĘĆ W TOKU — „widać, na kogo czekamy" (24.09.2026). Zdjęcie zmienia się w SPS
   * dopiero po potwierdzeniu przez wszystkie automaty, więc bez znacznika przy artykule jeden
   * wyłączony automat blokował zmianę bez żadnego śladu na liście.
   *
   * ⚠️ ODPYTUJEMY CZĘŚCIEJ TYLKO WTEDY, GDY COŚ WISI — pusta lista nie ma czego śledzić.
   */
  const wysylki = useQuery({
    queryKey: ['obrazki-wysylki', groupId],
    queryFn: () => fetchWysylkiObrazkow(groupId!),
    enabled: Boolean(groupId) && isAdmin,
    refetchInterval: (q) => ((q.state.data?.wysylki.length ?? 0) > 0 ? 15_000 : false),
  });
  const wysylkaTowaru = useMemo(() => {
    const m = new Map<string, WysylkaObrazka>();
    for (const w of wysylki.data?.wysylki ?? []) for (const n of w.towary) m.set(n, w);
    return m;
  }, [wysylki.data]);

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  // Filtry wskazujące rekordy TEJ wyspy muszą zniknąć przy przełączeniu wyspy — inaczej
  // zostaje id z poprzedniej i lista wraca cicho pusta.
  const resetFilters = useCallback(() => {
    setItemGroupId('');
    setAssigned('');
    setFill('');
    setStatus('active');
    setOnlyDuplicates(false);
  }, []);
  /**
   * CZYSZCZENIE FILTRÓW Z EKRANU — zeruje też `?grupa=` w adresie.
   *
   * ⚠️ BEZ TEGO FILTR WRACAŁBY PO ODŚWIEŻENIU: grupę czytamy z adresu przy wejściu, więc parametr
   * zostawiony w pasku nadpisałby świeżo wyczyszczony stan przy każdym F5.
   */
  const wyczyscFiltry = useCallback(() => {
    resetFilters();
    onStateChange({ page: 1 });
    if (szukajka.has('grupa')) {
      const bez = new URLSearchParams(szukajka);
      bez.delete('grupa');
      ustawSzukajke(bez, { replace: true });
    }
  }, [resetFilters, onStateChange, szukajka, ustawSzukajke]);

  const [lastIsland, setLastIsland] = useState(groupId);
  if (groupId !== lastIsland) {
    setLastIsland(groupId);
    resetFilters();
  }

  const params = {
    groupId: groupId!,
    search: state.search || undefined,
    itemGroupId: itemGroupId || undefined,
    assigned: assigned === '' ? undefined : assigned === 'true',
    fill: (fill || undefined) as 'empty' | 'low' | 'ok' | undefined,
    status,
    duplicateSupplierPart: onlyDuplicates,
    page: state.page,
    pageSize: state.pageSize,
    sort: state.sort,
    dir: state.dir,
  };

  const list = useQuery({
    queryKey: ['items', params],
    queryFn: () => listItems(params),
    enabled: !!groupId,
  });

  const groups = useQuery({
    queryKey: ['items', 'groups', groupId],
    queryFn: () => fetchItemGroups(groupId!),
    enabled: !!groupId,
  });

  /**
   * NAZWY CZYNNYCH FILTRÓW dla paska nad tabelą.
   *
   * ⚠️ „TYLKO AKTYWNE" NIE JEST FILTREM — to stan domyślny tego ekranu. Wpisane na pasek świeciłoby
   * zawsze i pasek przestałby cokolwiek znaczyć.
   */
  const grupaFiltra = (groups.data ?? []).find((g) => g.id === itemGroupId);
  const aktywneFiltry = [
    itemGroupId ? t('itm.group') + ': ' + (grupaFiltra ? groupLabel(grupaFiltra) : itemGroupId) : null,
    assigned === ''
      ? null
      : t('itm.assignment') + ': ' + (assigned === 'true' ? t('itm.onlyAssigned') : t('itm.onlyUnassigned')),
    fill ? t('itm.fill') + ': ' + t(fill === 'empty' ? 'itm.fillEmpty' : fill === 'low' ? 'itm.fillLow' : 'itm.fillOk') : null,
    status === 'active'
      ? null
      : t('itm.status') + ': ' + (status === 'obsolete' ? t('itm.statusObsolete') : t('itm.statusAll')),
    onlyDuplicates ? t('itm.onlyDuplicates') : null,
  ].filter((x): x is string => x !== null);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['items'] });

  const columns = useMemo<ColumnDef<ItemRow>[]>(
    () => [
      {
        id: 'itemNumber',
        header: t('itm.number'),
        meta: { exportValue: (r) => r.itemNumber },
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <span className="font-mono text-xs">{row.original.itemNumber}</span>
            {row.original.pending && (
              <Badge variant="warning" title={t('itm.pendingHint')}>
                <Clock className="size-3" />
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'name',
        header: t('itm.name'),
        meta: { exportValue: (r) => r.name },
        /**
         * ⚠️ BEZ MINIATURY NA LIŚCIE, i to jest decyzja, nie przeoczenie. Obrazek rzadko rozstrzyga
         * cokolwiek na liście, a dwadzieścia pięć wierszy to dwadzieścia pięć żądań o plik przy
         * każdym przewinięciu. Podgląd jest w oknie obrazka, gdzie ktoś świadomie o niego prosi.
         */
        cell: ({ row }) => (
          <button
            type="button"
            className="text-left hover:text-[var(--accent)] hover:underline"
            onClick={() => setDetail(row.original)}
          >
            {row.original.name}
            {row.original.obsolete && (
              <Badge variant="destructive" className="ml-1.5">
                {t('itm.obsolete')}
              </Badge>
            )}
          </button>
        ),
      },
      {
        id: 'groupName',
        header: t('itm.group'),
        meta: { exportValue: (r) => r.group?.name ?? '' },
        cell: ({ row }) =>
          row.original.group ? (
            <span className="text-xs">
              {row.original.group.name}
              {row.original.group.isSubGroup && (
                <span className="ml-1 text-muted-foreground" title={t('itm.isSubGroup')}>
                  ›
                </span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      /**
       * ⚠️ MIĘDZY GRUPĄ A NUMEREM U DOSTAWCY (23.09.2026, na wprost zgłoszone): numer u dostawcy
       * czyta się dopiero wtedy, gdy wiadomo, u KOGO — dostawca stoi więc tuż przed nim.
       *
       * ⚠️⚠️ NAZWA, A GDY JEJ NIE MA — NUMER. Dostawca spoza słownika jest u klienta stanem
       * POPRAWNYM: w SPS `Items.SUPPLIERNUMBER` nie ma klucza obcego do `Suppliers`. Pokazanie
       * wtedy kreski kazałoby szukać braku tam, gdzie go nie ma.
       */
      {
        id: 'supplier',
        header: t('itm.supplier'),
        meta: { exportValue: (r) => r.supplierName ?? r.supplierNumber ?? '' },
        cell: ({ row }) => {
          const r = row.original;
          if (r.supplierNumber === null) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="whitespace-nowrap">
              {r.supplierName ?? <span className="font-mono text-xs">{r.supplierNumber}</span>}
              {/* ⚠️ Numer spoza słownika mówi, że tego dostawcy nikt nie zdefiniował — bez alarmu. */}
              {r.supplierName === null && (
                <span className="ml-1 text-[11px] text-muted-foreground">{t('itm.supplierUnknown')}</span>
              )}
            </span>
          );
        },
      },
      {
        id: 'supplierPart',
        header: t('itm.supplierPart'),
        enableSorting: false,
        meta: { exportValue: (r) => r.supplierPartNumber ?? '' },
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <span className="font-mono text-xs text-muted-foreground">
              {row.original.supplierPartNumber ?? '—'}
            </span>
            {/* Dwa towary pod jednym numerem u dostawcy = ryzyko zamówienia złego towaru */}
            {row.original.supplierPartSharedWith > 0 && (
              <Badge variant="warning" title={t('itm.supplierPartSharedHint')}>
                <AlertTriangle className="size-3" />
                {row.original.supplierPartSharedWith + 1}
              </Badge>
            )}
          </span>
        ),
      },
      {
        /**
         * ⚠️⚠️ LICZBA AUTOMATÓW I ROZBICIE W JEDNEJ KOLUMNIE — połączone na wprost zgłoszone życzenie
         * (23.09.2026): osobna kolumna z samą ikoną zajmowała szerokość, a mówi o tej samej liczbie.
         * Rozbicie pokazuje, ILE TEGO TOWARU STOI NA KTÓREJ MASZYNIE, czyli rozwija dokładnie tę liczbę.
         * Wcześniej (29.08.2026) ikona stała tuż za „Zapełnieniem” — z tego samego powodu: należy do
         * liczby obok, a nie do ikon edycji na końcu wiersza.
         *
         * ⚠️ PRZYCISK PRZY KAŻDYM WIERSZU, także „nieprzypisanym” — dla konta bez uprawnień to jedyna
         * widoczna droga do szczegółów towaru (uzasadnienie przy kolumnie akcji niżej).
         */
        id: 'machines',
        header: t('itm.machines'),
        meta: { exportValue: (r) => r.machines, align: 'right' },
        cell: ({ row }) => {
          const r = row.original;
          const liczba = (() => {
            if (r.machines > 0) return <span className="tabular-nums">{r.machines}</span>;
            /**
             * Towar LEŻY w bębnie, ale nie ma wpisu w tabeli limitów automatu.
             *
             * To nie jest zwykły „nieprzypisany": w SPS `VendingMachineItems` trzyma limity
             * i próg ponownego zamówienia, więc taki towar wydaje się z automatu, a portal
             * nie ma dla niego ani limitu, ani progu. Pokazanie tu samego „nieprzypisany"
             * obok procentu zapełnienia czytałoby się jak sprzeczność.
             */
            if (r.compartments > 0) {
              return (
                <Badge variant="warning" title={t('itm.inDrumNoLimitHint')}>
                  <AlertTriangle className="size-3" />
                  {t('itm.inDrumNoLimit')}
                </Badge>
              );
            }
            return <span className="text-xs text-muted-foreground">{t('itm.notAssignedShort')}</span>;
          })();
          return (
            <span className="flex items-center justify-end gap-1">
              {liczba}
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                title={t('itm.breakdownTitle')}
                aria-label={`${t('itm.breakdownTitle')} — ${r.name}`}
                onClick={() => setDetail(r)}
              >
                <Boxes />
              </Button>
            </span>
          );
        },
      },
      {
        id: 'fillPercent',
        header: t('itm.fill'),
        meta: {
          exportValue: (r) => (r.fillPercent === null ? '' : `${r.fillPercent}%`),
        },
        cell: ({ row }) => {
          const r = row.original;
          if (r.compartments === 0) return <span className="text-xs text-muted-foreground">{t('itm.absentShort')}</span>;
          // brak pojemności = procentu NIE MA CZEGO liczyć; zero byłoby kłamstwem
          if (r.fillPercent === null) return <span className="text-xs text-muted-foreground">{t('itm.noCapacity')}</span>;
          return (
            <span className="flex items-center gap-2">
              <PasekZapelnienia percent={r.fillPercent} />
              <span className="tabular-nums text-xs text-muted-foreground">
                {r.fillPercent}% · {r.qtyCurrent}/{r.qtyMax}
              </span>
              {r.lowMachines > 0 && (
                <Badge variant="warning" title={t('itm.lowHint')}>
                  {r.lowMachines}
                </Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'limits',
        header: t('itm.limits'),
        meta: { exportValue: (r) => r.limits, align: 'right' },
        cell: ({ row }) => <span className="tabular-nums">{row.original.limits || '—'}</span>,
      },
      {
        id: 'txCount',
        header: t('itm.activity'),
        meta: { exportValue: (r) => `${r.txCount} / ${fmtDate(r.lastTxAt)}` },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {row.original.txCount > 0 ? (
              <>
                <b className="text-foreground">{row.original.txCount}</b> {t('itm.withdrawals')} ·{' '}
                {fmtDate(row.original.lastTxAt)}
              </>
            ) : (
              '—'
            )}
          </span>
        ),
      },
      /**
       * ⚠️ ROZBICIE NA AUTOMATY MA WŁASNY PRZYCISK I NIE JEST ZA UPRAWNIENIEM ADMINA.
       *
       * Rozbicie istniało od dawna, ale otwierało się WYŁĄCZNIE kliknięciem w nazwę towaru,
       * bez żadnego znaku, że nazwa jest klikalna (podkreślenie pojawiało się dopiero pod
       * kursorem). Kolumna akcji jest admin-only, więc dla kogoś bez uprawnień nie istniała
       * ŻADNA widoczna droga do tych danych. Zgłoszone jako „nie można tam zobaczyć per
       * maszyna" — i tak to wyglądało, mimo że dane były na wyciągnięcie jednego kliknięcia.
       */
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: '',
              enableSorting: false,
              cell: ({ row }) => (
                <span className="flex justify-end gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    title={t('common.edit')}
                    aria-label={`${t('common.edit')} ${row.original.name}`}
                    onClick={() => setEditing(row.original)}
                  >
                    <Pencil />
                  </Button>
                  {/* ⚠️ Obrazka NIE ma w tym samym oknie co edycja: to nie zmiana wiersza w bazie,
                      tylko wgranie pliku na KAŻDY automat wyspy — inny czas trwania i inny skutek. */}
                  {(() => {
                    const w = wysylkaTowaru.get(row.original.itemNumber);
                    if (w === undefined) return null;
                    const bladWysylki = w.bledy.length > 0;
                    const opis = bladWysylki
                      ? t('obraz.lista.blad').replace('{automaty}', w.bledy.map((b) => b.automat).join(', '))
                      : t('obraz.lista.czeka').replace('{automaty}', w.czekaNa.join(', '));
                    return (
                      /* ⚠️ Klik w znacznik otwiera to samo okno — od razu na stanie wysyłki. */
                      <button type="button" className="self-center" title={opis} onClick={() => setObrazekFor(row.original)}>
                        <Badge variant={bladWysylki ? 'destructive' : 'warning'}>
                          {bladWysylki ? t('obraz.lista.bladKrotko') : `${w.gotowe}/${w.razem}`}
                        </Badge>
                      </button>
                    );
                  })()}
                  <Button
                    variant="ghost"
                    size="icon"
                    title={t('obraz.akcja')}
                    aria-label={`${t('obraz.akcja')} ${row.original.name}`}
                    onClick={() => setObrazekFor(row.original)}
                  >
                    <ImageUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={t('itm.numberChangeAction')}
                    aria-label={`${t('itm.numberChangeAction')} ${row.original.name}`}
                    onClick={() => setNumerFor(row.original)}
                  >
                    <KeyRound />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={row.original.obsolete ? t('itm.restoreTitle') : t('itm.retireTitle')}
                    aria-label={`${row.original.obsolete ? t('itm.restoreTitle') : t('itm.retireTitle')} ${row.original.name}`}
                    onClick={() => setObsoleting(row.original)}
                  >
                    {row.original.obsolete ? <RotateCcw /> : <Ban />}
                  </Button>
                </span>
              ),
            } satisfies ColumnDef<ItemRow>,
          ]
        : []),
    ],
    [t, isAdmin, groupId, wysylkaTowaru],
  );

  const s = list.data?.summary;

  return (
    <>
      {/* Liczniki dotyczą CAŁEJ wyspy, niezależnie od filtrów — inaczej ten sam napis
          pokazywałby raz stan wyspy, a raz stan przefiltrowanego podzbioru. */}
      {s && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: t('itm.kpi.total'), value: s.total },
            { label: t('itm.kpi.assigned'), value: s.assigned },
            { label: t('itm.kpi.unassigned'), value: s.unassigned },
            { label: t('itm.kpi.lowStock'), value: s.lowStock, warn: s.lowStock > 0, hint: t('itm.lowHint') },
            { label: t('itm.kpi.obsolete'), value: s.obsolete },
            {
              label: t('itm.kpi.duplicateSupplierPart'),
              value: s.duplicateSupplierPart,
              warn: s.duplicateSupplierPart > 0,
              hint: t('itm.supplierPartSharedHint'),
              onClick: () => {
                setOnlyDuplicates(true);
                setStatus('all');
                onStateChange({ page: 1 });
              },
            },
          ].map((k) => (
            <button
              key={k.label}
              type="button"
              disabled={!k.onClick}
              onClick={k.onClick}
              title={k.hint}
              className="rounded-md border border-border bg-card p-2.5 text-left enabled:hover:border-[var(--accent)] disabled:cursor-default"
            >
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.label}</div>
              <div className={`mt-0.5 text-xl font-semibold tabular-nums ${k.warn ? 'text-[var(--warning)]' : ''}`}>
                {k.value}
              </div>
            </button>
          ))}
        </div>
      )}

      {/*
        ⚠️ BEZ RAMKI O PROGU „NA WYCZERPANIU" — zgłoszone wprost (28.08.2026). Mówiła, że liczba
        pomija przypisania bez `CRITICALLEVEL`. Zastrzeżenie zostaje tam, gdzie dotyczy JEDNEGO
        wiersza: `itm.noThresholdHint` przy automacie bez progu.
      */}

      <DataTable
        title={t('nav.items')}
        columns={columns}
        data={list.data?.rows ?? []}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={list.isPending}
        isFetching={list.isFetching}
        onRefresh={refresh}
        exportFileName="produkty"
        rowKey={(r) => r.id}
        emptyText={t('itm.empty')}
        actions={
          isAdmin ? (
            <div className="flex flex-wrap items-center gap-2">
              {/*
                ⚠️ „ZARZĄDZAJ ZDJĘCIAMI" W MIEJSCU „ZGRAJ OBRAZKI Z AUTOMATU" — zgłoszone
                31.08.2026. Zgrywanie zostało, ale WEWNĄTRZ okna: samo w sobie odpowiadało na
                pytanie „uzupełnij bibliotekę", a nie na to, które ludzie zadają przy automacie
                („czy TA maszyna ma ten obrazek"). Drugie wymaga pomiaru na maszynach i ma teraz
                swoje miejsce obok galerii.
              */}
              <Button variant="outline" disabled={!groupId} onClick={() => setZdjecia(true)}>
                <Images className="size-4" />
                {t('zdj.przycisk')}
              </Button>
              {/*
                ⚠️ SŁOWNIK DOSTAWCÓW OBOK BIBLIOTEKI ZDJĘĆ, bo to ten sam gatunek rzeczy: mały
                zbiór, z którego korzysta się PRZY towarze, a nie zamiast niego. Zmierzone na
                Tyco: dwa wpisy, w obu nazwa równa numerowi — na własną zakładkę to za mało.
              */}
              <Button variant="outline" disabled={!groupId} onClick={() => setDostawcyOtwarte(true)}>
                <Truck className="size-4" />
                {t('dost.przycisk')}
              </Button>
              <Button onClick={() => setAdding(true)}>
                <Plus /> {t('itm.add')}
              </Button>
            </div>
          ) : undefined
        }
        aktywneFiltry={aktywneFiltry}
        onWyczyscFiltry={wyczyscFiltry}
        filters={
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-xs">
              <span className="mb-1 block text-muted-foreground">{t('itm.group')}</span>
              <Select
                value={itemGroupId}
                onChange={(e) => {
                  setItemGroupId(e.target.value);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('itm.allGroups')}</option>
                {(groups.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {groupLabel(g)}
                  </option>
                ))}
              </Select>
            </label>

            <label className="text-xs">
              <span className="mb-1 block text-muted-foreground">{t('itm.assignment')}</span>
              <Select
                value={assigned}
                onChange={(e) => {
                  setAssigned(e.target.value);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('itm.anyAssignment')}</option>
                <option value="true">{t('itm.onlyAssigned')}</option>
                <option value="false">{t('itm.onlyUnassigned')}</option>
              </Select>
            </label>

            <label className="text-xs">
              <span className="mb-1 block text-muted-foreground">{t('itm.fill')}</span>
              <Select
                value={fill}
                onChange={(e) => {
                  setFill(e.target.value);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('itm.anyFill')}</option>
                <option value="empty">{t('itm.fillEmpty')}</option>
                <option value="low">{t('itm.fillLow')}</option>
                <option value="ok">{t('itm.fillOk')}</option>
              </Select>
            </label>

            <label className="text-xs">
              <span className="mb-1 block text-muted-foreground">{t('itm.status')}</span>
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as 'active' | 'obsolete' | 'all');
                  onStateChange({ page: 1 });
                }}
              >
                <option value="active">{t('itm.statusActive')}</option>
                <option value="obsolete">{t('itm.statusObsolete')}</option>
                <option value="all">{t('itm.statusAll')}</option>
              </Select>
            </label>

            <label className="flex items-center gap-2 text-xs sm:col-span-2">
              <input
                type="checkbox"
                checked={onlyDuplicates}
                onChange={(e) => {
                  setOnlyDuplicates(e.target.checked);
                  onStateChange({ page: 1 });
                }}
              />
              {t('itm.onlyDuplicates')}
            </label>

            {/* Rozkład towarów po grupach — dane i tak przychodzą z listą, więc pokazujemy
                je zamiast trzymać w odpowiedzi bez użytku. */}
            {(list.data?.byGroup ?? []).length > 0 && (
              <div className="sm:col-span-2 lg:col-span-4">
                <span className="mb-1 block text-xs text-muted-foreground">{t('itm.byGroup')}</span>
                <div className="space-y-1">
                  {(list.data?.byGroup ?? []).slice(0, 8).map((g) => {
                    const max = list.data!.byGroup[0]!.items || 1;
                    return (
                      <div key={g.id ?? 'none'} className="flex items-center gap-2 text-xs">
                        <span className="w-40 shrink-0 truncate text-muted-foreground" title={g.name}>
                          {g.name}
                        </span>
                        <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                          <span
                            className="block h-full rounded-full bg-[var(--accent)]"
                            style={{ width: `${Math.round((g.items / max) * 100)}%` }}
                          />
                        </span>
                        <span className="w-8 text-right tabular-nums text-muted-foreground">{g.items}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        }
      />

      {(adding || editing) && groupId && (
        <ItemModal
          editing={editing}
          groupId={groupId}
          groups={groups.data ?? []}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={refresh}
        />
      )}
      {zdjecia && groupId && (
        <ZarzadzanieZdjeciami groupId={groupId} mozeZapisywac={isAdmin} onClose={() => setZdjecia(false)} />
      )}
      {dostawcyOtwarte && groupId && (
        <ZarzadzanieDostawcami
          groupId={groupId}
          mozeZapisywac={isAdmin}
          onClose={() => setDostawcyOtwarte(false)}
        />
      )}
      {detail && groupId && <DetailModal item={detail} groupId={groupId} onClose={() => setDetail(null)} />}
      {obsoleting && groupId && (
        <ObsoleteModal
          item={obsoleting}
          groupId={groupId}
          onClose={() => setObsoleting(null)}
          onSaved={refresh}
        />
      )}
      {obrazekFor && groupId && (
        <ObrazekTowaru
          towarId={obrazekFor.id}
          islandId={groupId}
          numer={obrazekFor.itemNumber}
          nazwa={obrazekFor.name}
          indeks={obrazekFor.supplierPartNumber}
          obecny={obrazekFor.imageFile}
          maBajty={obrazekFor.hasImage}
          wysylka={wysylkaTowaru.get(obrazekFor.itemNumber) ?? null}
          onClose={() => setObrazekFor(null)}
        />
      )}
      {numerFor && groupId && (
        <ZmianaNumeruTowaru
          towarId={numerFor.id}
          islandId={groupId}
          obecnyNumer={numerFor.itemNumber}
          nazwa={numerFor.name}
          onClose={() => setNumerFor(null)}
          onSaved={refresh}
        />
      )}
    </>
  );
}
